import type { Directive } from 'vue';
import type { Size } from '@/types/objects';
import { isResized } from '@/helpers/dom';

// this type is based on ResizeObserverCallback
type DirectiveCallback = (entry: Element, observerEntry?: ResizeObserverEntry) => void | boolean;

interface DirectiveContext {
    once?: boolean;
    immediate?: boolean;
    size?: Size;
    handler?: DirectiveCallback;
}

interface DirectiveModifiers {
    once?: boolean;
    immediate?: boolean;
}

type ResizeDirective = {
    resizeDirective?: DirectiveContext;
};

// as we are adding a property to the Node interface, we need to extend it
declare global {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    interface Node extends ResizeDirective {}
}

function saveSize(el: HTMLElement) {
    // directive got unmounted
    if (!el.resizeDirective) return;
    el.resizeDirective.size = {
        width: el.offsetWidth,
        height: el.offsetHeight,
    };
}

const resizeObserver = new ResizeObserver((list) => {
    // filter for entries which size actually changed.
    // Sometimes, CSS changes or child elements can cause the observer to fire.
    const changedEntries = list.filter((entry) => {
        const target = entry.target as HTMLElement;
        const size = target.resizeDirective?.size;
        // we ignore changes of less than 1 pixel as they don't have a noticeable impact
        return size && isResized(target, size);
    });

    if (!changedEntries.length) return;

    // We delay execution of the handler to the next animation frame.
    // In case the handler manipulates the DOM, we don't end up in an error "ResizeObserver loop limit exceeded"
    // but have at most one execution per frame.
    window.requestAnimationFrame(() => {
        list.forEach((entry) => {
            const ctx = entry.target.resizeDirective;
            if (!ctx) return;

            saveSize(entry.target as HTMLElement);

            if (ctx.handler?.(entry.target, entry) === false || ctx.once === true) {
                // eslint-disable-next-line no-use-before-define
                destroy(entry.target);
            }
        });
    });
});

function destroy(el: Element) {
    if (!el.resizeDirective) return;

    resizeObserver.unobserve(el);
    delete el.resizeDirective;
}

function updateObserver(el: HTMLElement) {
    const ctx = el.resizeDirective;
    if (!ctx) return;

    resizeObserver.unobserve(el);
    saveSize(el);

    if (typeof ctx.handler === 'function') {
        resizeObserver.observe(el);
    }
}

export default {
    beforeMount(
        el: HTMLElement,
        { modifiers: { immediate, once }, value }: { modifiers: DirectiveModifiers; value: DirectiveCallback },
    ) {
        el.resizeDirective = {
            once,
            immediate,
            handler: value,
        };

        updateObserver(el);
    },

    mounted(el: HTMLElement, { modifiers: { immediate } }: { modifiers: DirectiveModifiers }) {
        if (immediate && el.resizeDirective?.handler) {
            el.resizeDirective.handler(el, undefined);
        }
    },

    updated(el: HTMLElement, { oldValue, value }: { oldValue: DirectiveCallback; value: DirectiveCallback }) {
        if (!el.resizeDirective || oldValue !== value) return;

        el.resizeDirective.handler = value;

        updateObserver(el);
    },

    unmounted: destroy,
} as Directive;
